import React, { useState, useEffect } from "react";
import Header from "../Components/Header";
import "./Cart.css";
import cartImg from "../assets/images/cartImg.png";
import Breadcrumb from "react-bootstrap/Breadcrumb";
import Feature from "../Components/Feature";
import Footer from "../Components/Footer";
import ship from "../assets/images/ship.png";
import secure from "../assets/images/secure.png";
import { Link, useNavigate } from "react-router-dom";
import axios from "../api/axios";

export default function Cart() {
  const navigate = useNavigate();
  const token = localStorage.getItem("access_token");
  const userStr = localStorage.getItem("user");
  const user = userStr ? JSON.parse(userStr) : null;
  const userId = user ? user.id : null;

  const [loading, setLoading] = useState(true);
  const [cartItems, setCartItems] = useState([]);
  const [cartSummary, setCartSummary] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const fetchCartData = async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    try {
      setErrorMsg("");
      // 1. Fetch Cart List
      const cartRes = await axios.post("/carts", { user_id: userId });
      let items = [];
      if (cartRes.data && cartRes.data.data) {
        cartRes.data.data.forEach((shop) => {
          if (shop.cart_items) {
            items = [...items, ...shop.cart_items];
          }
        });
      }
      setCartItems(items);

      // 2. Fetch Cart Summary
      const summaryRes = await axios.post("/cart-summary", { user_id: userId });
      setCartSummary(summaryRes.data);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching cart data:", err);
      setErrorMsg("Failed to load cart items.");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token || !userId) {
      navigate("/login");
      return;
    }
    fetchCartData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userId, navigate]);

  // Update Item Quantity
  const handleUpdateQty = async (cartItemId, currentQty, newQty, upperLimit) => {
    if (newQty < 1) return;
    if (newQty > upperLimit) {
      alert(`Only ${upperLimit} items are available in stock.`);
      return;
    }

    try {
      const res = await axios.post("/carts/change-quantity", {
        id: cartItemId,
        quantity: newQty,
      });

      if (res.data.result) {
        await fetchCartData();
        // Sync header count
        window.dispatchEvent(new Event("cart-updated"));
      } else {
        alert(res.data.message || "Failed to update quantity.");
      }
    } catch (err) {
      console.error("Error updating quantity:", err);
      alert("Failed to update cart quantity.");
    }
  };

  // Remove Item
  const handleRemoveItem = async (cartItemId) => {
    try {
      const res = await axios.delete(`/carts/${cartItemId}`);
      if (res.data.result) {
        await fetchCartData();
        // Sync header count
        window.dispatchEvent(new Event("cart-updated"));
      } else {
        alert(res.data.message || "Failed to remove item.");
      }
    } catch (err) {
      console.error("Error removing item:", err);
      alert("Failed to remove item from cart.");
    }
  };

  return (
    <>
      <Header mode="dark" />

      <div className="cart_main py-5">
        <div className="container-fluid px-4">
          <div className="breadcrumb_title detail_page_breadcrumb mb-4">
            <Breadcrumb>
              <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
              <Breadcrumb.Item href="/products">Shop</Breadcrumb.Item>
              <Breadcrumb.Item active>Cart</Breadcrumb.Item>
            </Breadcrumb>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-dark" role="status">
                <span className="visually-hidden">Loading cart...</span>
              </div>
              <h5 className="mt-3 text-muted">Loading your cart...</h5>
            </div>
          ) : errorMsg ? (
            <div className="alert alert-danger text-center my-4">{errorMsg}</div>
          ) : cartItems.length === 0 ? (
            <div className="text-center py-5 my-5">
              <h3 className="mb-3">Your Shopping Cart is Empty</h3>
              <p className="text-muted">Explore our shop to add beautiful textiles and rugs.</p>
              <Link to="/products" className="btn btn-dark mt-3 px-4 py-2">
                Continue Shopping
              </Link>
            </div>
          ) : (
            <div className="cart-page">
              <div className="row g-5">
                {/* Cart items list */}
                <div className="col-lg-8">
                  <h4 className="cart-title mb-4 border-bottom pb-2">Cart Items</h4>

                  {cartItems.map((item) => (
                    <div className="cart-item d-flex align-items-center py-4 border-bottom" key={item.id}>
                      <div
                        style={{
                          width: "110px",
                          height: "110px",
                          borderRadius: "8px",
                          overflow: "hidden",
                          backgroundColor: "#fff",
                          flexShrink: 0,
                        }}
                      >
                        <img
                          src={item.product_thumbnail_image || cartImg}
                          alt={item.product_name}
                          className="w-100 h-100"
                          style={{ objectFit: "cover" }}
                        />
                      </div>

                      <div className="cart-details ms-4 flex-grow-1">
                        <div className="cart-name fw-bold" style={{ fontSize: "16px" }}>
                          {item.product_name}
                        </div>
                        <div className="cart-meta text-muted small mt-1">
                          {item.variation && <span className="me-3">Variant: {item.variation}</span>}
                          {item.id && <span>Cart Ref: #{item.id}</span>}
                        </div>

                        <div className="mt-3 d-flex align-items-center gap-3">
                          <div className="qty-box d-flex align-items-center border rounded">
                            <button
                              className="btn btn-sm px-2 border-0"
                              onClick={() => handleUpdateQty(item.id, item.quantity, item.quantity - 1, item.upper_limit)}
                            >
                              -
                            </button>
                            <p className="number mb-0 px-2 fw-semibold">{item.quantity}</p>
                            <button
                              className="btn btn-sm px-2 border-0"
                              onClick={() => handleUpdateQty(item.id, item.quantity, item.quantity + 1, item.upper_limit)}
                            >
                              +
                            </button>
                          </div>
                          <span className="text-muted small">
                            Stock: {item.upper_limit} available
                          </span>
                        </div>
                      </div>

                      <div className="cart-price text-end fw-bold fs-5 d-flex flex-column align-items-end justify-content-between h-100">
                        <span>{item.price}</span>
                        <button
                          className="btn btn-link text-danger remove-btn mt-3 p-0 text-decoration-none"
                          style={{ fontSize: "20px", lineHeight: "1" }}
                          onClick={() => handleRemoveItem(item.id)}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Summary Section */}
                <div className="col-lg-4">
                  <div className="cart-summary bg-light p-4 rounded shadow-sm">
                    <div className="summary-title border-bottom pb-2 mb-4">Cart Total</div>

                    <div className="summary-row d-flex justify-content-between mb-3">
                      <span>Subtotal</span>
                      <span className="fw-semibold">{cartSummary?.sub_total || "₹0"}</span>
                    </div>

                    <div className="summary-row d-flex justify-content-between mb-3">
                      <span>Tax</span>
                      <span className="fw-semibold">{cartSummary?.tax || "₹0"}</span>
                    </div>

                    <div className="summary-row d-flex justify-content-between mb-3">
                      <span>Shipping</span>
                      <span className="fw-semibold text-success">
                        {cartSummary?.shipping_cost === "₹0" ? "Free" : cartSummary?.shipping_cost || "Free"}
                      </span>
                    </div>

                    <div className="summary-divider border-top my-3"></div>

                    <div className="summary-row summary-total d-flex justify-content-between align-items-center mb-4">
                      <span className="fs-5 fw-bold">Total</span>
                      <span className="fs-4 fw-bold">{cartSummary?.grand_total || "₹0"}</span>
                    </div>

                    <button
                      className="btn btn-dark w-100 py-3 fw-bold mb-3 pay-btn"
                      style={{ borderRadius: "8px" }}
                      onClick={() => navigate("/checkout")}
                    >
                      Proceed to Checkout
                    </button>
                    <button
                      className="btn btn-outline-dark w-100 py-2 back-btn"
                      style={{ borderRadius: "8px" }}
                      onClick={() => navigate("/products")}
                    >
                      Back to Shopping
                    </button>
                  </div>

                  <div className="info-box mt-4 p-3 bg-white rounded border">
                    <div className="info-item d-flex align-items-center mb-3">
                      <div className="info-icon me-3" style={{ width: "35px" }}>
                        <img src={ship} alt="Free Shipping" className="img-fluid" />
                      </div>
                      <div>
                        <div className="info-title fw-bold" style={{ fontSize: "14px" }}>Free Shipping</div>
                        <div className="info-sub text-muted small">On orders over ₹2000</div>
                      </div>
                    </div>

                    <div className="info-item d-flex align-items-center">
                      <div className="info-icon me-3" style={{ width: "35px" }}>
                        <img src={secure} alt="Secure Packaging" className="img-fluid" />
                      </div>
                      <div>
                        <div className="info-title fw-bold" style={{ fontSize: "14px" }}>Secure Packaging</div>
                        <div className="info-sub text-muted small">Art safely packed & insured</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Feature />
      <Footer />
    </>
  );
}
